/**
 * PATH: packages/bills/src/components/BillLineItemsEditor.tsx
 * FILE_ID: BILLS-PKG-CMP-LINEITEMS-EDITOR-001
 * PURPOSE: The ONE editable line-items grid for a bill — a tight, Excel-style
 *          row table (Description / Qty / Unit / Total) with per-cell edit,
 *          per-row delete, and "Add line". Renders the grid only (no titled
 *          section) so every caller wraps it in its own section chrome.
 * WHY:     Operator on the List inline detail: "I can't edit it in the row
 *          table." The Workspace `BillDataPanel` already had an editable grid;
 *          this lifts it into a shared component so the List drawer and the
 *          Workspace stay byte-identical instead of drifting into two editors.
 *          The per-cell "pick from PDF" crosshair buttons were removed
 *          (operator: too much clutter) — cells still carry data-bill-field,
 *          so clicking a cell arms document capture via the panel's click
 *          delegation.
 *
 * FLOW:
 *   items[] ──▶ render input grid ──▶ onChange(nextItems[])
 *             (update cell | remove row | add row → pure array transforms)
 *
 * DEPENDENCIES:
 *   - ../types: BillLineItem (shape persisted into the line_items JSON)
 *   - ./billFieldKeys: lineFieldKey (stable data-bill-field for cross-highlight)
 *   - lucide-react: X (remove), Plus (add)
 * RELATED:
 *   - BillDataPanel.tsx (Workspace 3-pane consumer)
 *   - feature/BillDrawer.tsx (List inline / drawer consumer)
 */

import { Plus, X } from 'lucide-react'
import type { BillLineItem } from '../types'
import { lineFieldKey, type BillFieldKey } from './billFieldKeys'

// 4 data columns + a 24px delete gutter. CSS grid (not per-cell widths) keeps
// the header and every row perfectly aligned. Description takes the remaining
// width (truncating); the numeric columns are fixed, right-aligned, and wide
// enough that totals are never clipped. The whole grid sits inside an
// overflow-x-auto + min-w wrapper so a very narrow pane scrolls instead of
// colliding the headers or cutting off the Total column.
const LINE_ITEM_GRID =
  'grid grid-cols-[minmax(0,1fr)_3.5rem_4.25rem_4.75rem_1.5rem] gap-1.5 items-center'

const cellClass =
  'w-full rounded-sm border border-transparent bg-transparent px-1.5 py-1 text-xs outline-none hover:border-border focus:border-primary focus:bg-background focus:ring-1 focus:ring-primary/40'

export interface BillLineItemsEditorProps {
  items: BillLineItem[]
  onChange: (items: BillLineItem[]) => void
  /** Read-only / mid-save: disables every input + button. */
  disabled?: boolean
}

/** f(items) → editable grid; emits a new array on every edit (no mutation). */
export function BillLineItemsEditor({
  items,
  onChange,
  disabled = false,
}: BillLineItemsEditorProps) {
  const updateAt = (idx: number, patch: Partial<BillLineItem>) => {
    const next = items.slice()
    next[idx] = { ...next[idx], ...patch }
    onChange(next)
  }
  const removeAt = (idx: number) => onChange(items.filter((_, i) => i !== idx))
  const addRow = () =>
    onChange([
      ...items,
      { description: '', quantity: null, unit_amount: null, line_total: null },
    ])

  return (
    <>
      <div className="overflow-x-auto rounded-md border border-border">
        <div className="min-w-[20rem]">
          <div
            className={
              LINE_ITEM_GRID +
              ' border-b border-border bg-muted/50 px-1.5 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground'
            }
          >
            <span className="truncate">Description</span>
            <span className="text-right">Qty</span>
            <span className="text-right">Unit</span>
            <span className="text-right">Total</span>
            <span aria-hidden />
          </div>

          {items.length === 0 ? (
            <p className="px-2 py-2 text-xs italic text-muted-foreground">
              No line items yet — click “Add line” to enter one.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((it, idx) => (
                <li key={idx} className={LINE_ITEM_GRID + ' px-1.5 py-1 hover:bg-accent/30'}>
                  <Cell
                    fieldKey={lineFieldKey(idx, 'description')}
                    value={(it.description as string) ?? ''}
                    placeholder="Description"
                    disabled={disabled}
                    onChange={(v) => updateAt(idx, { description: v })}
                  />
                  <Cell
                    fieldKey={lineFieldKey(idx, 'quantity')}
                    value={asString(it.quantity)}
                    placeholder="0"
                    numeric
                    disabled={disabled}
                    onChange={(v) => updateAt(idx, { quantity: toNumberOrNull(v) })}
                  />
                  <Cell
                    fieldKey={lineFieldKey(idx, 'unit_amount')}
                    value={asString(it.unit_amount)}
                    placeholder="0.00"
                    numeric
                    disabled={disabled}
                    onChange={(v) => updateAt(idx, { unit_amount: toNumberOrNull(v) })}
                  />
                  <Cell
                    fieldKey={lineFieldKey(idx, 'line_total')}
                    value={asString(it.line_total)}
                    placeholder="0.00"
                    numeric
                    disabled={disabled}
                    onChange={(v) => updateAt(idx, { line_total: toNumberOrNull(v) })}
                  />
                  <button
                    type="button"
                    onClick={() => removeAt(idx)}
                    disabled={disabled}
                    title="Remove line"
                    aria-label="Remove line"
                    className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-40"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={addRow}
        disabled={disabled}
        className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground hover:underline underline-offset-2 disabled:opacity-40"
      >
        <Plus className="h-3 w-3" /> Add line
      </button>
    </>
  )
}

/**
 * One grid cell. Carries ``data-bill-field`` so the panel's click/focus
 * delegation can arm document capture and the Read pane can cross-highlight.
 */
function Cell({
  fieldKey,
  value,
  placeholder,
  onChange,
  disabled,
  numeric = false,
}: {
  fieldKey: BillFieldKey
  value: string
  placeholder: string
  onChange: (value: string) => void
  disabled?: boolean
  numeric?: boolean
}) {
  return (
    <input
      data-bill-field={fieldKey}
      className={cellClass + (numeric ? ' text-right tabular-nums' : '')}
      placeholder={placeholder}
      inputMode={numeric ? 'decimal' : undefined}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

function asString(v: unknown): string {
  if (v == null) return ''
  return String(v)
}

function toNumberOrNull(v: string): number | null {
  if (!v.trim()) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

export default BillLineItemsEditor
